/**
 * Browser desktop notifications. Wraps the Notification API with three
 * gates: (1) browser support, (2) user permission granted, (3) the
 * current tab is NOT focused. The third gate is the whole point of
 * desktop notifications — when the agent is already looking at the app,
 * the in-app indicators (typing dots, unread badges, sound) are enough.
 */

export type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function getPermission(): PermissionState {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Prompt the user for notification permission. Browsers only allow this
 * from a user gesture (click/keydown), so call it from a button handler.
 * Returns the resulting state — caller can show a confirmation toast.
 */
export async function requestPermission(): Promise<PermissionState> {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

interface ShowOptions {
  body?: string;
  icon?: string;
  /** Tag dedupe — same-tag notifications stack into one. Use the
   *  conversation_id so multiple incoming messages from the same chat
   *  don't pile a notification per message. */
  tag?: string;
  onClick?: () => void;
  /** Keep the OS notification visible until the user interacts with it.
   *  Chromium supports this hint; the persistent in-app alert remains the
   *  cross-browser guarantee when the OS/browser ignores it. */
  requireInteraction?: boolean;
}

/** Constructor-created notifications we can explicitly close when the matching
 * in-app acknowledgement button is pressed. Service-worker notifications are
 * outside this module and are not used by the realtime manager. */
const activeNotifications = new Map<string, Notification>();

export function closeNotification(tag: string): void {
  const notification = activeNotifications.get(tag);
  if (!notification) return;
  activeNotifications.delete(tag);
  notification.close();
}

export function closeAllNotifications(): void {
  for (const notification of activeNotifications.values()) notification.close();
  activeNotifications.clear();
}

/**
 * Fire a desktop notification. Returns silently if any gate fails so
 * callers can call this unconditionally on every event.
 */
export function showNotification(title: string, opts: ShowOptions = {}): void {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  // Respect "tab focused" — desktop notifications only make sense when
  // the user is on another tab/window/app.
  if (document.hasFocus()) return;

  // The in-app UI groups alerts by channel; mirror that behavior at OS level
  // and retain the instance so “Entendido” can close it explicitly.
  if (opts.tag) closeNotification(opts.tag);

  const n = new Notification(title, {
    body: opts.body,
    icon: opts.icon ?? '/favicon.ico',
    tag: opts.tag,
    requireInteraction: opts.requireInteraction,
  });
  if (opts.tag) activeNotifications.set(opts.tag, n);

  n.onclose = () => {
    if (opts.tag && activeNotifications.get(opts.tag) === n) {
      activeNotifications.delete(opts.tag);
    }
  };

  n.onclick = () => {
    window.focus();
    if (opts.tag) closeNotification(opts.tag);
    else n.close();
    opts.onClick?.();
  };

  if (!opts.requireInteraction) {
    // Transient notifications keep the original behavior. Internal team-chat
    // alerts opt into requireInteraction and therefore have no client timer.
    window.setTimeout(() => {
      if (opts.tag) closeNotification(opts.tag);
      else n.close();
    }, 6000);
  }
}
